import React, { useState, useEffect } from 'react';
import { X, Users, Lock, Building2, Check, Loader2, Search, Plus } from 'lucide-react';
import { supabase } from '../services/supabaseService';

export interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemId: string;
    itemType: 'documents' | 'document_files' | 'vacancies' | 'cv_design_templates';
    currentVisibility: 'private' | 'company' | 'shared' | 'reviewers_only' | 'subcontractors';
    currentSharedUsers: string[];
    currentSharedClients?: string[];
    onSave: (visibility: string, sharedUsers: string[], sharedClients?: string[]) => void;
    clientId?: string;
    additionalClientIds?: string[];
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, itemId, itemType, currentVisibility, currentSharedUsers, currentSharedClients, onSave, clientId, additionalClientIds }) => {
    const [visibility, setVisibility] = useState(currentVisibility || 'private');
    const [sharedUsers, setSharedUsers] = useState<string[]>(currentSharedUsers || []);
    const [sharedClients, setSharedClients] = useState<string[]>(currentSharedClients || []);
    const [colleagues, setColleagues] = useState<any[]>([]);
    const [clientsList, setClientsList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (isOpen) {
            setVisibility(currentVisibility || 'private');
            setSharedUsers(currentSharedUsers || []);
            fetchColleagues();
        }
    }, [isOpen, currentVisibility, currentSharedUsers]);

    const fetchColleagues = async () => {
        setLoading(true);
        try {
            // Either fetch by client_id if available or all in the same org
            // Using profiles table
            let query = supabase.from('profiles').select('id, name, email, role');
            const allClientIds = [clientId, ...(additionalClientIds || [])].filter(Boolean).filter(id => id !== 'system');
            if (allClientIds.length > 0) {
                query = query.in('client_id', allClientIds);
            }
            const { data } = await query;
            if (data) setColleagues(data);

            if (itemType === 'vacancies') {
                // If a clientId is provided, restrict subcontractors to those where parent_client_id matches clientId
                // or if no clientId, fetch all (Admin view).
                let clientsQuery = supabase.from('clients').select('id, name, client_type').neq('id', clientId || '00000000-0000-0000-0000-000000000000');
                if (clientId) {
                    clientsQuery = clientsQuery.eq('parent_client_id', clientId);
                }
                const { data: clientsData } = await clientsQuery;
                if (clientsData) setClientsList(clientsData);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const toggleUser = (userId: string) => {
        if (sharedUsers.includes(userId)) {
            setSharedUsers(sharedUsers.filter(id => id !== userId));
        } else {
            setSharedUsers([...sharedUsers, userId]);
        }
    };

    const toggleClient = (cId: string) => {
        if (sharedClients.includes(cId)) {
            setSharedClients(sharedClients.filter(id => id !== cId));
        } else {
            setSharedClients([...sharedClients, cId]);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const updates: any = { visibility };
            if (visibility === 'shared') {
                updates.shared_with_users = sharedUsers;
            } else {
                updates.shared_with_users = [];
            }

            if (itemType === 'vacancies') {
                updates.shared_with_clients = visibility === 'subcontractors' ? sharedClients : [];
            }

            // Only Document files currently use document_files, others use normal table names
            const { error } = await supabase.from(itemType).update(updates).eq('id', itemId);
            if (error) throw error;

            onSave(visibility, visibility === 'shared' ? sharedUsers : [], visibility === 'subcontractors' ? sharedClients : []);
            onClose();
        } catch (e) {
            console.error(e);
            alert("Fout bij opslaan delen instellingen");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const filteredColleagues = colleagues.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="fixed inset-0 z-[300] flex justify-end bg-slate-900/60 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
            <div className="bg-white w-full max-w-md h-full shadow-2xl overflow-hidden animate-slideInRight flex flex-col border-l border-slate-200" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-red- text-red- flex items-center justify-center">
                            <Users size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-snug">Toegang Beheren</h2>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Wie heeft toegang?</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-200 rounded-full transition-colors"><X size={20} /></button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                    <div className="space-y-3 mb-8">
                        {/* Legacy visibility options completely removed to match new inline builder */}
                    </div>

                    <div className="animate-fadeIn bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                                <Users size={12} /> Team Samenstellen
                            </h4>

                            <div className="relative mb-6">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                    <input
                                        type="text"
                                        placeholder="Zoek recruiter om toe te voegen..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] shadow-sm transition-all"
                                    />
                                </div>
                                {search.trim() !== '' && filteredColleagues.filter(c => !sharedUsers.includes(c.id)).length > 0 && (
                                     <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto z-[60] p-2 space-y-1">
                                          {filteredColleagues.filter(c => !sharedUsers.includes(c.id)).map(c => (
                                              <div key={c.id} onClick={() => { toggleUser(c.id); setSearch(''); }} className="flex items-center gap-3 p-2 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-lg cursor-pointer transition-all">
                                                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-700">{c.name?.charAt(0) || c.email?.charAt(0)}</div>
                                                  <div>
                                                      <p className="text-sm font-bold text-slate-900">{c.name || c.email}</p>
                                                      <p className="text-[10px] text-slate-500 uppercase font-black">{c.role || 'Gebruiker'}</p>
                                                  </div>
                                              </div>
                                          ))}
                                     </div>
                                )}
                            </div>

                            <div className="space-y-3">
                                {sharedUsers.length === 0 ? (
                                    <div className="p-4 text-center text-xs font-medium text-slate-500 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                                        Nog niemand geselecteerd.
                                    </div>
                                ) : (
                                    colleagues.filter(c => sharedUsers.includes(c.id)).map(colleague => (
                                        <div key={colleague.id} className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50 shadow-sm transition-all hover:border-slate-300">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-sm font-bold text-slate-700 border border-slate-200 shrink-0">
                                                    {colleague.name?.charAt(0) || colleague.email?.charAt(0)}
                                                </div>
                                                <div className="truncate">
                                                    <span className="text-sm font-black text-slate-900 block truncate">{colleague.name || colleague.email}</span>
                                                    <span className="text-xs text-slate-500 uppercase tracking-widest font-bold block truncate">{colleague.role || 'Gebruiker'}</span>
                                                </div>
                                            </div>
                                            <button onClick={() => toggleUser(colleague.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-red-100 shadow-sm">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                    {visibility === 'subcontractors' && itemType === 'vacancies' && (
                        <div className="animate-fadeIn">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-purple-600 mb-3">Werfplaats / Agencies Ophalen</h4>
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                <input
                                    type="text"
                                    placeholder="Zoek agency..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium outline-none focus:border-purple-400"
                                />
                            </div>

                            {loading ? <div className="text-center py-4"><Loader2 className="animate-spin text-slate-400 mx-auto" /></div> : (
                                <div className="space-y-1 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {clientsList.filter(c => c.name.toLowerCase().includes(search.toLowerCase())).map(c => (
                                        <div key={c.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer" onClick={() => toggleClient(c.id)}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 border border-purple-200">
                                                    {c.name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-800">{c.name}</p>
                                                    <p className="text-[10px] text-slate-500 uppercase font-black">{c.client_type}</p>
                                                </div>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={sharedClients.includes(c.id)}
                                                onChange={() => { }}
                                                className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                                            />
                                        </div>
                                    ))}
                                    {clientsList.length === 0 && <p className="text-xs text-center text-slate-500 mt-4">Geen onderaannemers gevonden.</p>}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 mt-auto">
                    <button onClick={onClose} className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors">Annuleren</button>
                    <button onClick={handleSave} disabled={isSaving} className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2">
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        Opslaan
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
